import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

const MAX_QUERY_HISTORY = 30;
const STORAGE_KEY = "globe-data.tables.ai-query-history";

const TablesAIQueryContext = createContext(null);

const createQueryEntry = (query) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  query,
  status: "loading",
  createdAt: Date.now(),
  response: null,
  error: null,
});

const readStoredHistory = () => {
  if (typeof window === "undefined") {
    return { entries: [], activeEntryId: null };
  }
  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return { entries: [], activeEntryId: null };
    }
    const parsedValue = JSON.parse(rawValue);
    const entries = Array.isArray(parsedValue.entries)
      ? parsedValue.entries.slice(0, MAX_QUERY_HISTORY)
      : [];
    const activeEntryId = entries.some((entry) => entry.id === parsedValue.activeEntryId)
      ? parsedValue.activeEntryId
      : entries[0]?.id || null;
    return { entries, activeEntryId };
  } catch {
    return { entries: [], activeEntryId: null };
  }
};

export function TablesAIQueryProvider({ children }) {
  const { fetchWithAuth } = useAuth();
  const [draftQuery, setDraftQuery] = useState("");
  const [entries, setEntries] = useState(() => readStoredHistory().entries);
  const [activeEntryId, setActiveEntryId] = useState(() => readStoredHistory().activeEntryId);
  const [displayedEntryId, setDisplayedEntryId] = useState(null);

  const runQuery = useCallback(
    async (query) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) {
        return null;
      }

      const nextEntry = createQueryEntry(trimmedQuery);
      setEntries((current) => [nextEntry, ...current].slice(0, MAX_QUERY_HISTORY));
      setActiveEntryId(nextEntry.id);
      setDisplayedEntryId(nextEntry.id);

      try {
        const response = await fetchWithAuth("/ai/ai-query", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: trimmedQuery,
            limit: 100,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.detail || "AI query failed");
        }

        const data = await response.json();
        setEntries((current) =>
          current.map((entry) =>
            entry.id === nextEntry.id
              ? {
                  ...entry,
                  status: "success",
                  response: data,
                }
              : entry
          )
        );
        return data;
      } catch (error) {
        setEntries((current) =>
          current.map((entry) =>
            entry.id === nextEntry.id
              ? {
                  ...entry,
                  status: "error",
                  error: error.message,
                }
              : entry
          )
        );
        throw error;
      }
    },
    [fetchWithAuth]
  );

  const activeEntry = useMemo(
    () => entries.find((entry) => entry.id === activeEntryId) || null,
    [activeEntryId, entries]
  );
  const displayedEntry = useMemo(
    () => entries.find((entry) => entry.id === displayedEntryId) || null,
    [displayedEntryId, entries]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          entries: entries.slice(0, MAX_QUERY_HISTORY),
          activeEntryId,
        })
      );
    } catch {}
  }, [activeEntryId, entries]);

  const displayEntry = useCallback((entryId) => {
    setActiveEntryId(entryId);
    setDisplayedEntryId(entryId);
  }, []);

  const clearDisplayedEntry = useCallback(() => {
    setDisplayedEntryId(null);
  }, []);

  const value = useMemo(
    () => ({
      draftQuery,
      setDraftQuery,
      entries,
      activeEntryId,
      activeEntry,
      displayedEntryId,
      displayedEntry,
      setActiveEntryId,
      displayEntry,
      clearDisplayedEntry,
      runQuery,
    }),
    [activeEntry, activeEntryId, clearDisplayedEntry, displayEntry, displayedEntry, displayedEntryId, draftQuery, entries, runQuery]
  );

  return <TablesAIQueryContext.Provider value={value}>{children}</TablesAIQueryContext.Provider>;
}

export function useTablesAIQuery() {
  const context = useContext(TablesAIQueryContext);
  if (!context) {
    throw new Error("useTablesAIQuery must be used within a TablesAIQueryProvider");
  }
  return context;
}
